'use client';

import { useEffect, useId, useState } from 'react';
import { BlockRoot } from '@akademi/site-builder-runtime';
import { Icon } from '../shared/Icon';
import {
  headingLevel,
  items,
  surfaceClass,
  text,
  type HeadingLevel,
  type Localized,
  type Surface,
} from '../shared/primitives';
import { SkBaseStyles, SkStyles } from '../styles/SkStyles';

/**
 * Customer stories: a quote carousel with the customer's name and town.
 *
 * <h3>Works without JavaScript</h3>
 * The server renders EVERY quote as a stacked list. Only after hydration does
 * the block switch itself into a one-at-a-time carousel with previous/next
 * buttons and a position readout. A visitor without JavaScript, a crawler and
 * the first paint all get the complete list.
 *
 * <h3>Accessibility</h3>
 * The carousel follows the WAI-ARIA carousel pattern: a labelled region with
 * `aria-roledescription="carousel"`, slides as groups ("2 of 4"), and a polite
 * live region announcing the change. It never auto-rotates.
 */
export interface SpatialKitchenStory {
  quote?: Localized;
  name?: Localized;
  town?: Localized;
  /** Optional project detail, e.g. "Shaker kitchen, painted in Sage". */
  project?: Localized;
}

export interface SpatialKitchenStoriesProps {
  eyebrow?: Localized;
  title?: Localized;
  stories?: SpatialKitchenStory[];
  previousLabel?: Localized;
  nextLabel?: Localized;
  surface?: Surface;
  headingLevel?: HeadingLevel;
}

const CSS = `
.sk-stories__inner{display:grid;gap:2rem;max-width:56rem;margin-inline:auto;text-align:center}
.sk-stories__mark{color:var(--sk-sage-deep);display:flex;justify-content:center}
.sk-stories__list{display:grid;gap:3rem}
.sk-story{margin:0;display:grid;gap:1.5rem}
.sk-story blockquote{margin:0}
.sk-story__quote{font-family:var(--sk-serif);font-weight:300;font-size:clamp(1.375rem,1.1rem + 1.1vw,2.125rem);line-height:1.35;color:var(--sk-ink);margin:0}
.sk-story__cite{font-style:normal;display:grid;gap:.125rem;font-size:.9375rem}
.sk-story__name{font-weight:500;color:var(--sk-ink)}
.sk-story__meta{color:var(--sk-muted)}
.sk-stories--carousel .sk-story[hidden]{display:none}
.sk-stories__controls{display:flex;align-items:center;justify-content:center;gap:1rem}
.sk-stories__btn{display:inline-grid;place-items:center;width:2.75rem;height:2.75rem;border-radius:999px;border:1px solid var(--sk-control-border);background:transparent;color:var(--sk-ink);cursor:pointer;transition:background-color .18s ease,color .18s ease}
.sk-stories__btn:hover{background:var(--sk-ink);color:#fff;border-color:var(--sk-ink)}
.sk-stories__status{min-width:4.5rem;font-size:.875rem;color:var(--sk-muted);font-variant-numeric:tabular-nums}
`;

export function SpatialKitchenStories(props: SpatialKitchenStoriesProps) {
  const stories = items<SpatialKitchenStory>(props.stories).filter((story) => text(story.quote));
  const title = text(props.title, 'Customer stories');
  const level = headingLevel(props.headingLevel, 'h2');
  const Title = level;
  const previousLabel = text(props.previousLabel, 'Previous story');
  const nextLabel = text(props.nextLabel, 'Next story');
  const titleId = useId();
  const listId = useId();

  // False on the server and on the first client render, so hydration matches
  // the stacked list; flips to the carousel once JavaScript is running.
  const [enhanced, setEnhanced] = useState(false);
  const [active, setActive] = useState(0);
  useEffect(() => setEnhanced(true), []);

  const carousel = enhanced && stories.length > 1;
  const count = stories.length;
  const go = (delta: number) => setActive((current) => (current + delta + count) % count);

  return (
    <BlockRoot
      as="section"
      className={`sk sk-stories sk-section ${surfaceClass(props.surface, 'sage-grey')}${carousel ? ' sk-stories--carousel' : ''}`}
      aria-labelledby={titleId}
      {...(carousel ? { 'aria-roledescription': 'carousel' } : {})}
    >
      <SkBaseStyles />
      <SkStyles id="stories" css={CSS} />
      <div className="sk-container">
        <div className="sk-stories__inner">
          <div className="sk-section-head sk-section-head--centre" style={{ marginBottom: 0 }}>
            {text(props.eyebrow) && <p className="sk-eyebrow">{text(props.eyebrow)}</p>}
            <Title id={titleId} className="sk-heading sk-h2">
              {title}
            </Title>
          </div>
          <div className="sk-stories__mark">
            <Icon name="quote" size={36} />
          </div>
          <div id={listId} className="sk-stories__list" {...(carousel ? { 'aria-live': 'polite' as const } : {})}>
            {stories.map((story, index) => {
              const meta = [text(story.town), text(story.project)].filter(Boolean).join(' · ');
              return (
                <figure
                  key={`${text(story.name)}-${index}`}
                  className="sk-story"
                  hidden={carousel && index !== active}
                  {...(carousel
                    ? { role: 'group', 'aria-roledescription': 'slide', 'aria-label': `${index + 1} of ${count}` }
                    : {})}
                >
                  <blockquote>
                    <p className="sk-story__quote">{text(story.quote)}</p>
                  </blockquote>
                  <figcaption className="sk-story__cite">
                    {text(story.name) && <span className="sk-story__name">{text(story.name)}</span>}
                    {meta && <span className="sk-story__meta">{meta}</span>}
                  </figcaption>
                </figure>
              );
            })}
          </div>
          {carousel && (
            <div className="sk-stories__controls">
              <button
                type="button"
                className="sk-stories__btn"
                aria-controls={listId}
                aria-label={previousLabel}
                onClick={() => go(-1)}
              >
                <Icon name="arrow-left" size={18} />
              </button>
              <span className="sk-stories__status" aria-hidden="true">
                {active + 1} / {count}
              </span>
              <button
                type="button"
                className="sk-stories__btn"
                aria-controls={listId}
                aria-label={nextLabel}
                onClick={() => go(1)}
              >
                <Icon name="arrow-right" size={18} />
              </button>
            </div>
          )}
        </div>
      </div>
    </BlockRoot>
  );
}
